import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import reporteService from '../services/reporteService';

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'depositado', label: 'Depositado' },
  { value: 'con_novedad', label: 'Con Novedad' },
  { value: 'reprogramado', label: 'Reprogramado' },
  { value: 'rechazado', label: 'Rechazado' },
];

const ESTADO_COLORS = {
  pendiente: 'warning',
  depositado: 'success',
  con_novedad: 'error',
  reprogramado: 'info',
  rechazado: 'default',
};

const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  depositado: 'Depositado',
  con_novedad: 'Con Novedad',
  reprogramado: 'Reprogramado',
  rechazado: 'Rechazado',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'USD' }).format(value || 0);

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-ES');
};

const ClienteRow = ({ clienteData }) => {
  const [expanded, setExpanded] = useState(false);
  const { cliente, cheques, total_cheques, monto_total } = clienteData;

  if (total_cheques === 0) return null;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', bgcolor: expanded ? '#f5f5f5' : 'inherit' }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {cliente.nombre}
              </Typography>
              {cliente.ruc && (
                <Typography variant="caption" color="textSecondary">
                  RUC: {cliente.ruc}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip label={total_cheques} color="primary" size="small" />
        </TableCell>
        <TableCell align="right">
          <Typography fontWeight={600} color="primary">
            {formatCurrency(monto_total)}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#eeeeee' }}>
                    <TableCell><strong>N° Cheque</strong></TableCell>
                    <TableCell><strong>Banco</strong></TableCell>
                    <TableCell><strong>N° Cuenta</strong></TableCell>
                    <TableCell><strong>Dueño de Cuenta</strong></TableCell>
                    <TableCell align="right"><strong>Valor</strong></TableCell>
                    <TableCell align="center"><strong>Estado</strong></TableCell>
                    <TableCell align="center"><strong>Fecha Pago</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cheques.map((cheque) => (
                    <TableRow key={cheque.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {cheque.numero_cheque}
                        </Typography>
                      </TableCell>
                      <TableCell>{cheque.banco}</TableCell>
                      <TableCell>{cheque.numero_cuenta_origen}</TableCell>
                      <TableCell>
                        {cheque.nombre_dueno_cuenta}
                        {cheque.nombre_dueno_cuenta !== clienteData.cliente.nombre && (
                          <Tooltip title="Dueño de cuenta distinto al cliente">
                            <Chip label="Distinto" size="small" color="warning" sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(cheque.monto)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={ESTADO_LABELS[cheque.estado] || cheque.estado}
                          color={ESTADO_COLORS[cheque.estado] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{formatDate(cheque.fecha_pago)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell colSpan={4} align="right">
                      <strong>Subtotal:</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(monto_total)}</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ReportClientesPage = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    nombreCliente: '',
    estado: '',
    fechaDesde: '',
    fechaHasta: '',
  });
  const { showError } = useNotification();

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async (activeFilters = filters) => {
    try {
      setLoading(true);
      const params = {};
      if (activeFilters.estado) params.estado = activeFilters.estado;
      if (activeFilters.fechaDesde) params.fechaDesde = activeFilters.fechaDesde;
      if (activeFilters.fechaHasta) params.fechaHasta = activeFilters.fechaHasta;

      const result = await reporteService.getClientesChecksReport(params);
      setReportData(result.data || result);
    } catch (error) {
      console.error('Error al cargar reporte de clientes:', error);
      showError('Error al cargar el reporte de clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => loadReport(filters);

  const handleClearFilters = () => {
    const clean = { nombreCliente: '', estado: '', fechaDesde: '', fechaHasta: '' };
    setFilters(clean);
    loadReport(clean);
  };

  const handleExportExcel = async () => {
    try {
      const blob = await reporteService.exportReportToExcel('clientes', filters);
      reporteService.downloadReport(blob, 'reporte-clientes.xlsx');
    } catch {
      showError('Error al exportar el reporte a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const blob = await reporteService.exportReportToPDF('clientes', filters);
      reporteService.downloadReport(blob, 'reporte-clientes.pdf');
    } catch {
      showError('Error al exportar el reporte a PDF');
    }
  };

  const clientesConCheques = (reportData?.clientes?.filter((c) => c.total_cheques > 0) || []).filter(
    (c) =>
      !filters.nombreCliente ||
      c.cliente.nombre.toLowerCase().includes(filters.nombreCliente.toLowerCase()),
  );

  if (loading && !reportData) {
    return <LoadingSpinner message="Cargando reporte de clientes..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Reporte de Cheques por Cliente"
        subtitle="Consulta todos los clientes y los cheques registrados a su nombre o como dueño de cuenta"
        breadcrumbs={[
          { label: 'Inicio', path: '/dashboard' },
          { label: 'Reportes', path: '/reportes' },
          { label: 'Por Cliente' },
        ]}
      />

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterListIcon color="action" />
          <Typography variant="h6">Filtros</Typography>
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nombre del Cliente"
              size="small"
              value={filters.nombreCliente}
              onChange={(e) => handleFilterChange('nombreCliente', e.target.value)}
              fullWidth
              placeholder="Buscar por nombre..."
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.estado}
                label="Estado"
                onChange={(e) => handleFilterChange('estado', e.target.value)}
              >
                {ESTADOS.map((e) => (
                  <MenuItem key={e.value} value={e.value}>
                    {e.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Fecha Desde"
              type="date"
              size="small"
              value={filters.fechaDesde}
              onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Fecha Hasta"
              type="date"
              size="small"
              value={filters.fechaHasta}
              onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                Filtrar
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} disabled={loading}>
                Limpiar Filtros
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={!reportData || loading}
            size="small"
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportPDF}
            disabled={!reportData || loading}
            size="small"
          >
            Exportar PDF
          </Button>
        </Box>
      </Paper>

      {/* Tarjetas de resumen */}
      {reportData && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Clientes
                  </Typography>
                  <Typography variant="h4">{reportData.totales?.total_clientes || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Clientes con Cheques
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {reportData.totales?.clientes_con_cheques || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Cheques
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {reportData.totales?.total_cheques || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f3e5f5' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Monto Total
                  </Typography>
                  <Typography variant="h6" color="secondary.main" fontWeight={700}>
                    {formatCurrency(reportData.totales?.monto_total || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabla principal */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Clientes con Cheques Registrados
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Haga clic en una fila para ver el detalle de los cheques
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell width={48} />
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell align="center"><strong>Cant. Cheques</strong></TableCell>
                    <TableCell align="right"><strong>Monto Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientesConCheques.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          No se encontraron clientes con cheques registrados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    clientesConCheques.map((clienteData) => (
                      <ClienteRow key={clienteData.cliente.id} clienteData={clienteData} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {!reportData && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">Cargando datos del reporte...</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReportClientesPage;
